import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Github, Terminal, CheckCircle2, Code2, Zap, Shield, Cpu, Database, Layout, Smartphone, Globe, Server, Rocket, Clock, Users, Star, GitBranch, Layers, FileCode, Workflow } from "lucide-react";
import { TechStackGrid } from "@/components/TechStackGrid";
import { Button } from "@/components/ui/button";
import ProceduralGroundBackground from "@/components/ui/procedural-ground-background";

// Projetos de desenvolvimento
const devProjects = [
  {
    id: "1",
    title: "Casa e Lazer",
    description: "E-commerce completo para produtos de casa e decoração com catálogo dinâmico, carrinho de compras, checkout com múltiplos meios de pagamento e painel administrativo.",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=500&fit=crop",
    stack: ["Next.js", "Supabase", "Stripe", "Tailwind"],
    features: ["Catálogo de produtos", "Carrinho persistente", "Checkout seguro", "Painel admin"],
    demoUrl: "#",
    githubUrl: "#",
    type: "E-commerce",
  },
  {
    id: "2",
    title: "CondômTrack",
    description: "Sistema SaaS para gestão de condomínios com controle de moradores, áreas comuns, financeiro, ocorrências e comunicação integrada.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop",
    stack: ["React", "Node.js", "PostgreSQL", "Socket.io"],
    features: ["Multi-tenant", "Chat em tempo real", "Relatórios financeiros", "App mobile"],
    demoUrl: "#",
    githubUrl: "#",
    type: "SaaS",
  },
  {
    id: "3",
    title: "VibéOffice",
    description: "Workspace colaborativo para equipes remotas com gestão de projetos, documentos compartilhados, videoconferência e integração com ferramentas externas.",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=500&fit=crop",
    stack: ["Next.js", "Prisma", "Redis", "WebRTC"],
    features: ["Colaboração real-time", "Video calls", "Kanban boards", "Integrações"],
    demoUrl: "#",
    githubUrl: "#",
    type: "Workspace",
  },
  {
    id: "4",
    title: "Dashboard Analytics",
    description: "Painel de business intelligence com visualização de dados em tempo real, relatórios customizáveis e alertas automatizados.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop",
    stack: ["React", "Python", "FastAPI", "AWS"],
    features: ["Gráficos interativos", "Exportação de dados", "Alertas customizados", "API REST"],
    demoUrl: "#",
    githubUrl: "#",
    type: "Dashboard",
  },
];

// Serviços de desenvolvimento
const services = [
  {
    icon: Layout,
    title: "Frontend Development",
    description: "Interfaces modernas e responsivas com as melhores práticas de UX/UI",
    techs: ["React", "Next.js", "TypeScript", "Tailwind CSS"]
  },
  {
    icon: Server,
    title: "Backend Development",
    description: "APIs robustas e escaláveis com arquitetura de microsserviços",
    techs: ["Node.js", "Python", "PostgreSQL", "Redis"]
  },
  {
    icon: Database,
    title: "Database & Data",
    description: "Modelagem de dados avançada e pipelines de processamento de dados",
    techs: ["PostgreSQL", "MongoDB", "Elasticsearch", "Redis"]
  },
  {
    icon: Smartphone,
    title: "Mobile & PWA",
    description: "Aplicativos mobile e Progressive Web Apps performáticos",
    techs: ["React Native", "PWA", "iOS", "Android"]
  },
  {
    icon: Layers,
    title: "Cloud & DevOps",
    description: "Infraestrutura como código e automação de deployments",
    techs: ["AWS", "Docker", "Kubernetes", "CI/CD"]
  },
  {
    icon: Workflow,
    title: "Integrações & APIs",
    description: "Conexão com serviços de terceiros e desenvolvimento de APIs REST/GraphQL",
    techs: ["REST", "GraphQL", "Webhooks", "Third-party APIs"]
  },
];

// Estatísticas
const stats = [
  {
    icon: Code2,
    value: "50+",
    label: "Projetos Entregues",
    description: "Sistemas em produção"
  },
  {
    icon: GitBranch,
    value: "1000+",
    label: "Commits",
    description: "Código de alta qualidade"
  },
  {
    icon: Users,
    value: "20+",
    label: "Clientes Satisfeitos",
    description: "Parcerias de longo prazo"
  },
  {
    icon: Star,
    value: "4.9",
    label: "Avaliação Média",
    description: "Satisfação dos clientes"
  },
];

// Processo de trabalho
const workProcess = [
  {
    step: "01",
    icon: Rocket,
    title: "Discovery",
    description: "Entendimento profundo do problema, requisitos e objetivos do projeto",
    items: ["Levantamento de requisitos", "Análise de stakeholders", "Definição de escopo", "Prototipagem inicial"],
    color: "from-blue-500 to-indigo-500"
  },
  {
    step: "02",
    icon: FileCode,
    title: "Design & Arquitetura",
    description: "Arquitetura da solução, design de APIs e prototipação das interfaces",
    items: ["Arquitetura do sistema", "Design de APIs", "Wireframes e protótipos", "Design System"],
    color: "from-indigo-500 to-purple-500"
  },
  {
    step: "03",
    icon: Terminal,
    title: "Desenvolvimento Ágil",
    description: "Implementação iterativa com sprints, code review e entregas frequentes",
    items: ["Sprints de 2 semanas", "Code review", "Testes automatizados", "CI/CD"],
    color: "from-purple-500 to-pink-500"
  },
  {
    step: "04",
    icon: Shield,
    title: "Deploy & Suporte",
    description: "Lançamento em produção e acompanhamento contínuo do sistema",
    items: ["Deploy automatizado", "Monitoramento 24/7", "Suporte técnico", "SLA garantido"],
    color: "from-pink-500 to-red-500"
  },
];

// Por que escolher
const whyChoose = [
  {
    icon: Zap,
    title: "Performance First",
    description: "Código otimizado para máxima performance e SEO"
  },
  {
    icon: Shield,
    title: "Segurança Avançada",
    description: "Implementação de melhores práticas de segurança"
  },
  {
    icon: Cpu,
    title: "Escalável",
    description: "Arquitetura preparada para crescer com seu negócio"
  },
  {
    icon: Clock,
    title: "Entrega Pontual",
    description: "Metodologia ágil com prazos realistas e cumpridos"
  },
];

export default function Desenvolvimento() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative">
      <ProceduralGroundBackground />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <section className="section-padding relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] opacity-10 pointer-events-none">
            <div className="w-full h-full bg-blue-500 blur-[150px] rounded-full" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-8 text-sm font-medium glassmorphism-card text-blue-400 rounded-full"
            >
              <Code2 className="w-4 h-4" />
              Desenvolvimento Fullstack
            </motion.div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8">
              Sistemas Robustos,{" "}
              <span className="gradient-text-primary">
                Experiências Fluidas
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto">
              Desenvolvimento web completo do conceito ao deploy. Aplicações escaláveis,
              performáticas e com código limpo que geram valor real para o seu negócio.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="max-w-2xl mx-auto mb-12"
            >
              <div className="glassmorphism-card rounded-2xl overflow-hidden border border-blue-500/20">
                <div className="flex items-center gap-2 px-6 py-3 bg-black/30 border-b border-white/10">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="ml-3 text-sm text-muted-foreground font-mono">~ desenvolvimento/cassio-projects</span>
                </div>
                <div className="p-6 space-y-3 font-mono text-sm">
                  <div className="flex items-center gap-3">
                    <Terminal className="w-4 h-4 text-blue-400" />
                    <span className="text-muted-foreground">$</span>
                    <span className="text-blue-400">npm run build</span>
                    <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Terminal className="w-4 h-4 text-blue-400" />
                    <span className="text-muted-foreground">$</span>
                    <span>deploying to production...</span>
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ repeat: Infinity, duration: 0.8 }}
                      className="w-2 h-4 bg-blue-400 ml-1"
                    />
                  </div>
                  <div className="flex items-center gap-3 text-green-400">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>✓ Build successful in 45s</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="liquid-glass-btn-solid rounded-full px-8 h-14 text-lg" asChild>
                <a href="#projetos">
                  Ver Projetos
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button variant="outline" size="lg" className="liquid-glass-outline rounded-full px-8 h-14 text-lg" asChild>
                <Link to="/contato">Solicitar Orçamento</Link>
              </Button>
            </div>
          </motion.div>
        </section>

        <section className="section-padding relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Números que <span className="gradient-text-primary">Importam</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Resultados reais que refletem a qualidade do trabalho entregue
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="glassmorphism-card rounded-2xl p-8 text-center card-hover-effect">
                  <stat.icon className="h-10 w-10 text-blue-400 mx-auto mb-4" />
                  <div className="text-4xl font-bold mb-2 gradient-text-primary">
                    {stat.value}
                  </div>
                  <div className="heading-card-sm text-white mb-2">{stat.label}</div>
                  <p className="text-sm text-muted-foreground">{stat.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="section-padding bg-muted/20 rounded-3xl my-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              O Que <span className="gradient-text-primary">Entrego</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Soluções completas de desenvolvimento sob medida para suas necessidades
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="glassmorphism-card rounded-2xl p-8 h-full card-hover-effect">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-xl mb-6 group-hover:scale-110 transition-transform">
                    <service.icon className="h-7 w-7 text-blue-400" />
                  </div>
                  <h3 className="heading-card mb-3 text-white">{service.title}</h3>
                  <p className="text-muted-foreground mb-6">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.techs.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 text-xs font-medium bg-blue-500/10 text-blue-400 rounded-full border border-blue-500/20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="projetos" className="section-padding relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Projetos em <span className="gradient-text-primary">Destaque</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Sistemas completos desenvolvidos com foco em performance, escalabilidade e experiência do usuário
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {devProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="glassmorphism-card rounded-2xl overflow-hidden h-full card-hover-effect group">
                  <div className="aspect-video overflow-hidden relative">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-2 text-xs font-medium bg-blue-500 text-white rounded-full border border-blue-400/20">
                        {project.type}
                      </span>
                    </div>
                  </div>

                  <div className="p-8">
                    <h3 className="heading-card-lg mb-3 text-white">{project.title}</h3>
                    <p className="text-muted-foreground mb-6">{project.description}</p>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.stack.map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1.5 text-xs font-medium bg-indigo-500/10 text-indigo-400 rounded-lg border border-indigo-500/20"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {project.features.slice(0, 4).map((feature) => (
                        <div key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <Button size="sm" className="liquid-glass-btn-solid flex-1" asChild>
                        <a href={project.demoUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Demo
                        </a>
                      </Button>
                      <Button size="sm" variant="outline" className="liquid-glass-outline flex-1" asChild>
                        <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                          <Github className="w-4 h-4 mr-2" />
                          Código
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Por Que <span className="gradient-text-primary">Escolher</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Diferenciais que garantem a qualidade e o sucesso do seu projeto
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="glassmorphism-card rounded-2xl p-8 h-full">
                  <item.icon className="h-10 w-10 text-indigo-400 mx-auto mb-4" />
                  <h3 className="heading-card-sm mb-3 text-white">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="section-padding relative overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Processo de <span className="gradient-text-primary">Trabalho</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Metodologia ágil com entregas frequentes e comunicação transparente
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workProcess.map((phase, index) => (
              <motion.div
                key={phase.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                <div className="glassmorphism-card rounded-2xl p-8 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-5xl font-bold text-white/10">{phase.step}</span>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${phase.color} flex items-center justify-center`}>
                      <phase.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  <h3 className="heading-card mb-3 text-white">{phase.title}</h3>
                  <p className="text-muted-foreground text-sm mb-6">{phase.description}</p>

                  <ul className="space-y-3">
                    {phase.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {index < workProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-8 bg-indigo-500/20 rounded-full flex items-center justify-center border border-indigo-500/30">
                      <ArrowRight className="w-4 h-4 text-indigo-400" />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        <section className="section-padding mb-20">
          <div className="glassmorphism-card rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/10 blur-[100px] rounded-full" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative z-10"
            >
              <Code2 className="h-12 w-12 text-blue-400 mx-auto mb-6" />
              <h2 className="heading-section mb-6 text-white">
                Tem um Projeto em <span className="gradient-text-primary">Mente?</span>
              </h2>
              <p className="text-body-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
                Vamos conversar sobre como posso ajudar a transformar sua ideia em um sistema funcional,
                escalável e que gera valor real para o seu negócio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="liquid-glass-btn-solid rounded-full px-8 h-14 text-lg" asChild>
                  <Link to="/contato">
                    Solicitar Orçamento
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" className="liquid-glass-outline rounded-full px-8 h-14 text-lg" asChild>
                  <Link to="/sobre">
                    Saiba Mais
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
}
